'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export default function BrowserTileCalculator() {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage('tile-calculator', '').catch(() => undefined);
  };

  const [roomLengthFt, setRoomLengthFt] = useState(10);
  const [roomWidthFt, setRoomWidthFt] = useState(8);
  const [tileLengthIn, setTileLengthIn] = useState(12);
  const [tileWidthIn, setTileWidthIn] = useState(12);
  const [wastePercent, setWastePercent] = useState(10);
  const [tilesPerBox, setTilesPerBox] = useState(10);

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => {
    const roomAreaSqFt = roomLengthFt * roomWidthFt;
    const tileAreaSqFt = (tileLengthIn * tileWidthIn) / 144;
    const rawTileCount = tileAreaSqFt > 0 ? roomAreaSqFt / tileAreaSqFt : 0;
    const tilesWithWaste = Math.ceil(rawTileCount * (1 + wastePercent / 100));
    const boxes = tilesPerBox > 0 ? Math.ceil(tilesWithWaste / tilesPerBox) : 0;
    return { roomAreaSqFt, tilesWithWaste, boxes };
  }, [roomLengthFt, roomWidthFt, tileLengthIn, tileWidthIn, wastePercent, tilesPerBox]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Tile</span>
        <h2 className="tool-live-title">Tile calculator</h2>
        <p className="tool-live-subtitle">
          Enter a room&apos;s area and tile size to get how many tiles (and boxes) you need for a
          floor or wall.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
        >
          <label className="tool-live-field">
            <span>Room length (ft)</span>
            <input
              min={0}
              onChange={(e) => onChange(setRoomLengthFt)(Number(e.target.value) || 0)}
              step={0.1}
              type="number"
              value={roomLengthFt}
            />
          </label>
          <label className="tool-live-field">
            <span>Room width (ft)</span>
            <input
              min={0}
              onChange={(e) => onChange(setRoomWidthFt)(Number(e.target.value) || 0)}
              step={0.1}
              type="number"
              value={roomWidthFt}
            />
          </label>
          <label className="tool-live-field">
            <span>Tile length (in)</span>
            <input
              min={0}
              onChange={(e) => onChange(setTileLengthIn)(Number(e.target.value) || 0)}
              step={0.5}
              type="number"
              value={tileLengthIn}
            />
          </label>
          <label className="tool-live-field">
            <span>Tile width (in)</span>
            <input
              min={0}
              onChange={(e) => onChange(setTileWidthIn)(Number(e.target.value) || 0)}
              step={0.5}
              type="number"
              value={tileWidthIn}
            />
          </label>
          <label className="tool-live-field">
            <span>Waste allowance (%)</span>
            <input
              min={0}
              onChange={(e) => onChange(setWastePercent)(Number(e.target.value) || 0)}
              type="number"
              value={wastePercent}
            />
          </label>
          <label className="tool-live-field">
            <span>Tiles per box (optional)</span>
            <input
              min={0}
              onChange={(e) => onChange(setTilesPerBox)(Number(e.target.value) || 0)}
              type="number"
              value={tilesPerBox}
            />
          </label>
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>{result.roomAreaSqFt.toFixed(1)} sq ft</strong>
            <span>room area</span>
          </p>
          <p className="tool-live-panel-note">
            <strong>{result.tilesWithWaste} tiles</strong>
            <span>including a {wastePercent}% cutting allowance</span>
          </p>
          {tilesPerBox > 0 ? (
            <p className="tool-live-panel-note">
              <strong>{result.boxes} boxes</strong>
              <span>at {tilesPerBox} tiles per box</span>
            </p>
          ) : null}
          <p className="tool-live-panel-note">
            A diagonal (herringbone or offset) layout usually needs more waste allowance, 15-20%
            rather than 10%.
          </p>
        </section>
      </div>
    </div>
  );
}
